import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faEye,
  faExclamationTriangle,
  faUsers,
  faServer,
  faChartLine,
  faBan,
  faUnlock,
  faSearch,
  faTrash,
  faRefresh,
  faClock,
  faGlobe,
  faRobot,
  faUserShield,
  faInfoCircle,
  faCheckCircle,
  faTimesCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../../services/api';
import './SecurityManagement.css';

interface SecurityDashboard {
  period_days: number;
  failed_logins: number;
  successful_logins: number;
  blocked_ips: number;
  high_risk_events: number;
  recent_events: SecurityEvent[];
  suspicious_ips: { ip_address: string; event_count: number }[];
}

interface SecurityEvent {
  id: number;
  user_id?: number;
  event_type: string;
  event_description: string;
  ip_address: string;
  user_agent?: string;
  email_attempted?: string;
  success: boolean;
  risk_level: string;
  additional_data?: any;
  created_at: string;
}

interface BlockedIP {
  id: number;
  ip_address: string;
  reason: string;
  block_type: string;
  failed_attempts: number;
  blocked_at: string;
  expires_at?: string;
  is_blocked: boolean;
  blocked_by_admin: boolean;
  unblocked_at?: string;
}

interface AuditLogsResponse {
  total: number;
  page: number;
  limit: number;
  pages: number;
  logs: SecurityEvent[];
}

const SecurityManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'audit' | 'blocked' | 'statistics'>('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dashboard state
  const [dashboard, setDashboard] = useState<SecurityDashboard | null>(null);
  const [dashboardDays, setDashboardDays] = useState(7);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLogsResponse | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditFilters, setAuditFilters] = useState({
    event_type: '',
    risk_level: '',
    ip_address: '',
    days: 7
  });
  const [eventTypes, setEventTypes] = useState<string[]>([]);

  // Blocked IPs state
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [includeExpired, setIncludeExpired] = useState(false);
  const [blockIPForm, setBlockIPForm] = useState({
    ip_address: '',
    reason: '',
    permanent: false,
    hours: 24
  });
  const [showBlockForm, setShowBlockForm] = useState(false);

  // Statistics state
  const [statistics, setStatistics] = useState<any>(null);
  const [statsDays, setStatsDays] = useState(30);

  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dashboardDays]);

  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, auditPage]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.allSettled([
        loadDashboard(),
        loadEventTypes()
      ]);
    } catch (err) {
      console.error('Initial data load error:', err);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      console.log('Loading security dashboard for', dashboardDays, 'days...');
      const data = await adminAPI.getSecurityDashboard(dashboardDays);
      console.log('Dashboard data received:', data);
      setDashboard(data);
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Dashboard load error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to load security dashboard';
      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed - please log in again';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied - admin privileges required';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error - security tables may not be initialized';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const data = await adminAPI.getAuditLogs(auditPage, 50, auditFilters);
      setAuditLogs(data);
    } catch (err) {
      setError('Failed to load audit logs');
    }
  };

  const loadBlockedIPs = async () => {
    try {
      const data = await adminAPI.getBlockedIPs(includeExpired);
      setBlockedIPs(data.blocked_ips);
    } catch (err) {
      setError('Failed to load blocked IPs');
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await adminAPI.getSecurityStatistics(statsDays);
      setStatistics(data);
    } catch (err) {
      setError('Failed to load statistics');
    }
  };

  const loadEventTypes = async () => {
    try {
      const data = await adminAPI.getEventTypes();
      setEventTypes(data.event_types);
    } catch (err) {
      console.error('Failed to load event types:', err);
      // Don't set error for this since it's not critical
    }
  };

  const handleBlockIP = async () => {
    if (!blockIPForm.ip_address || !blockIPForm.reason) {
      setError('IP address and reason are required');
      return;
    }

    try {
      await adminAPI.blockIP(
        blockIPForm.ip_address,
        blockIPForm.reason,
        blockIPForm.permanent,
        blockIPForm.hours
      );
      setShowBlockForm(false);
      setBlockIPForm({ ip_address: '', reason: '', permanent: false, hours: 24 });
      loadBlockedIPs();
      setError(null);
    } catch (err) {
      setError('Failed to block IP address');
    }
  };

  const handleUnblockIP = async (ipAddress: string) => {
    try {
      await adminAPI.unblockIP(ipAddress, 'Admin unblock');
      loadBlockedIPs();
      setError(null);
    } catch (err) {
      setError('Failed to unblock IP address');
    }
  };

  const handleCleanupLogs = async () => {
    if (!window.confirm('Are you sure you want to clean up old security logs? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await adminAPI.cleanupSecurityLogs(90);
      alert(`Cleanup completed: ${result.deleted_logs} logs, ${result.deleted_blocks} blocks, ${result.deleted_trackers} trackers deleted`);
      loadDashboard();
    } catch (err) {
      setError('Failed to cleanup logs');
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return <FontAwesomeIcon icon={faExclamationTriangle} className="risk-critical" />;
      case 'high': return <FontAwesomeIcon icon={faExclamationTriangle} className="risk-high" />;
      case 'medium': return <FontAwesomeIcon icon={faInfoCircle} className="risk-medium" />;
      default: return <FontAwesomeIcon icon={faCheckCircle} className="risk-low" />;
    }
  };

  const getEventIcon = (eventType: string) => {
    if (eventType.includes('login')) return faUsers;
    if (eventType.includes('password')) return faUnlock;
    if (eventType.includes('activation')) return faUserShield;
    if (eventType.includes('blocked')) return faBan;
    if (eventType.includes('suspicious')) return faRobot;
    return faInfoCircle;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const tabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'audit':
        return renderAuditLogs();
      case 'blocked':
        return renderBlockedIPs();
      case 'statistics':
        return renderStatistics();
      default:
        return null;
    }
  };

  const renderDashboard = () => (
    <div className="security-dashboard">
      <div className="dashboard-header">
        <h3><FontAwesomeIcon icon={faChartLine} /> Security Dashboard</h3>
        <div className="dashboard-controls">
          <select 
            value={dashboardDays} 
            onChange={(e) => setDashboardDays(Number(e.target.value))}
            className="period-select"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button onClick={loadDashboard} className="refresh-btn">
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
        </div>
      </div>

      {dashboard && (
        <>
          <div className="security-metrics">
            <div className="metric-card">
              <div className="metric-icon success">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="metric-info">
                <div className="metric-value">{dashboard.successful_logins}</div>
                <div className="metric-label">Successful Logins</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon failed">
                <FontAwesomeIcon icon={faTimesCircle} />
              </div>
              <div className="metric-info">
                <div className="metric-value">{dashboard.failed_logins}</div>
                <div className="metric-label">Failed Logins</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon blocked">
                <FontAwesomeIcon icon={faBan} />
              </div>
              <div className="metric-info">
                <div className="metric-value">{dashboard.blocked_ips}</div>
                <div className="metric-label">Blocked IPs</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon warning">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="metric-info">
                <div className="metric-value">{dashboard.high_risk_events}</div>
                <div className="metric-label">High Risk Events</div>
              </div>
            </div>
          </div>

          <div className="dashboard-content">
            <div className="recent-events">
              <h4><FontAwesomeIcon icon={faClock} /> Recent Security Events</h4>
              <div className="events-list">
                {dashboard.recent_events.slice(0, 10).map(event => (
                  <div key={event.id} className={`event-item risk-${event.risk_level}`}>
                    <div className="event-icon">
                      <FontAwesomeIcon icon={getEventIcon(event.event_type)} />
                    </div>
                    <div className="event-details">
                      <div className="event-description">{event.event_description}</div>
                      <div className="event-meta">
                        <span className="event-ip">
                          <FontAwesomeIcon icon={faGlobe} /> {event.ip_address}
                        </span>
                        <span className="event-time">
                          <FontAwesomeIcon icon={faClock} /> {formatTimestamp(event.created_at)}
                        </span>
                        {event.email_attempted && (
                          <span className="event-email">
                            <FontAwesomeIcon icon={faUsers} /> {event.email_attempted}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="event-risk">
                      {getRiskLevelIcon(event.risk_level)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="suspicious-ips">
              <h4><FontAwesomeIcon icon={faExclamationTriangle} /> Suspicious IPs</h4>
              <div className="suspicious-list">
                {dashboard.suspicious_ips.map(ip => (
                  <div key={ip.ip_address} className="suspicious-item">
                    <div className="ip-address">
                      <FontAwesomeIcon icon={faGlobe} /> {ip.ip_address}
                    </div>
                    <div className="event-count">
                      <span className="count-badge">{ip.event_count}</span> failed attempts
                    </div>
                    <button 
                      onClick={() => {
                        setBlockIPForm({ ...blockIPForm, ip_address: ip.ip_address, reason: 'Suspicious activity' });
                        setShowBlockForm(true);
                      }}
                      className="block-btn"
                    >
                      <FontAwesomeIcon icon={faBan} /> Block
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Continue in next part due to length...
  
  const renderAuditLogs = () => (
    <div className="audit-logs">
      <div className="audit-header">
        <h3><FontAwesomeIcon icon={faEye} /> Security Audit Logs</h3>
        <div className="audit-controls">
          <div className="filters">
            <select 
              value={auditFilters.event_type} 
              onChange={(e) => setAuditFilters({...auditFilters, event_type: e.target.value})}
              className="filter-select"
            >
              <option value="">All Event Types</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            <select 
              value={auditFilters.risk_level} 
              onChange={(e) => setAuditFilters({...auditFilters, risk_level: e.target.value})}
              className="filter-select"
            >
              <option value="">All Risk Levels</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            
            <input
              type="text"
              placeholder="Filter by IP address"
              value={auditFilters.ip_address}
              onChange={(e) => setAuditFilters({...auditFilters, ip_address: e.target.value})}
              className="filter-input"
            />
            
            <select 
              value={auditFilters.days} 
              onChange={(e) => setAuditFilters({...auditFilters, days: Number(e.target.value)})}
              className="filter-select"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            
            <button onClick={() => {
              setAuditPage(1); // Reset to first page when searching
              loadAuditLogs();
            }} className="filter-btn">
              <FontAwesomeIcon icon={faSearch} /> Search
            </button>
          </div>
        </div>
      </div>

      {auditLogs && (
        <>
          <div className="audit-pagination">
            <span>
              Showing {((auditLogs.page - 1) * auditLogs.limit) + 1} to {Math.min(auditLogs.page * auditLogs.limit, auditLogs.total)} of {auditLogs.total} events
            </span>
            <div className="pagination-controls">
              <button 
                disabled={auditLogs.page <= 1}
                onClick={() => setAuditPage(auditLogs.page - 1)}
              >
                Previous
              </button>
              <span>Page {auditLogs.page} of {auditLogs.pages}</span>
              <button 
                disabled={auditLogs.page >= auditLogs.pages}
                onClick={() => setAuditPage(auditLogs.page + 1)}
              >
                Next
              </button>
            </div>
          </div>

          <div className="audit-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>IP Address</th>
                  <th>Email</th>
                  <th>Risk</th>
                  <th>Success</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.logs.map(log => (
                  <tr key={log.id} className={`risk-${log.risk_level}`}>
                    <td className="time-cell">
                      {formatTimestamp(log.created_at)}
                    </td>
                    <td className="event-cell">
                      <div className="event-type">
                        <FontAwesomeIcon icon={getEventIcon(log.event_type)} />
                        {log.event_type}
                      </div>
                      <div className="event-desc">{log.event_description}</div>
                    </td>
                    <td className="ip-cell">
                      <FontAwesomeIcon icon={faGlobe} /> {log.ip_address}
                    </td>
                    <td className="email-cell">
                      {log.email_attempted || '-'}
                    </td>
                    <td className="risk-cell">
                      {getRiskLevelIcon(log.risk_level)}
                      {log.risk_level}
                    </td>
                    <td className="success-cell">
                      {log.success ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="success" />
                      ) : (
                        <FontAwesomeIcon icon={faTimesCircle} className="failed" />
                      )}
                    </td>
                    <td className="details-cell">
                      {log.additional_data && (
                        <details>
                          <summary>View</summary>
                          <pre>{JSON.stringify(log.additional_data, null, 2)}</pre>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const renderBlockedIPs = () => (
    <div className="blocked-ips">
      <div className="blocked-header">
        <h3><FontAwesomeIcon icon={faBan} /> Blocked IP Addresses</h3>
        <div className="blocked-controls">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeExpired}
              onChange={(e) => setIncludeExpired(e.target.checked)}
            />
            Include expired blocks
          </label>
          <button 
            onClick={() => setShowBlockForm(true)}
            className="block-new-btn"
          >
            <FontAwesomeIcon icon={faBan} /> Block New IP
          </button>
          <button onClick={loadBlockedIPs} className="refresh-btn">
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
        </div>
      </div>

      {showBlockForm && (
        <div className="block-form-overlay">
          <div className="block-form">
            <h4>Block IP Address</h4>
            <div className="form-group">
              <label>IP Address:</label>
              <input
                type="text"
                value={blockIPForm.ip_address}
                onChange={(e) => setBlockIPForm({...blockIPForm, ip_address: e.target.value})}
                placeholder="192.168.1.100"
              />
            </div>
            <div className="form-group">
              <label>Reason:</label>
              <input
                type="text"
                value={blockIPForm.reason}
                onChange={(e) => setBlockIPForm({...blockIPForm, reason: e.target.value})}
                placeholder="Suspicious activity"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={blockIPForm.permanent}
                  onChange={(e) => setBlockIPForm({...blockIPForm, permanent: e.target.checked})}
                />
                Permanent block
              </label>
            </div>
            {!blockIPForm.permanent && (
              <div className="form-group">
                <label>Duration (hours):</label>
                <input
                  type="number"
                  value={blockIPForm.hours}
                  onChange={(e) => setBlockIPForm({...blockIPForm, hours: Number(e.target.value)})}
                  min="1"
                  max="8760"
                />
              </div>
            )}
            <div className="form-actions">
              <button onClick={handleBlockIP} className="confirm-btn">
                <FontAwesomeIcon icon={faBan} /> Block IP
              </button>
              <button onClick={() => setShowBlockForm(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="blocked-list">
        {blockedIPs.length === 0 ? (
          <div className="no-blocks">
            <FontAwesomeIcon icon={faCheckCircle} />
            <p>No blocked IP addresses found</p>
          </div>
        ) : (
          <table className="blocked-table">
            <thead>
              <tr>
                <th>IP Address</th>
                <th>Reason</th>
                <th>Type</th>
                <th>Failed Attempts</th>
                <th>Blocked At</th>
                <th>Expires At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blockedIPs.map(block => (
                <tr key={block.id} className={block.is_blocked ? 'active-block' : 'expired-block'}>
                  <td className="ip-cell">
                    <FontAwesomeIcon icon={faGlobe} /> {block.ip_address}
                  </td>
                  <td className="reason-cell">{block.reason}</td>
                  <td className="type-cell">
                    <span className={`block-type ${block.block_type}`}>
                      {block.block_type}
                    </span>
                  </td>
                  <td className="attempts-cell">{block.failed_attempts}</td>
                  <td className="time-cell">{formatTimestamp(block.blocked_at)}</td>
                  <td className="time-cell">
                    {block.expires_at ? formatTimestamp(block.expires_at) : 'Never'}
                  </td>
                  <td className="status-cell">
                    {block.is_blocked ? (
                      <span className="status active">
                        <FontAwesomeIcon icon={faBan} /> Active
                      </span>
                    ) : (
                      <span className="status expired">
                        <FontAwesomeIcon icon={faCheckCircle} /> Expired
                      </span>
                    )}
                  </td>
                  <td className="actions-cell">
                    {block.is_blocked && (
                      <button 
                        onClick={() => handleUnblockIP(block.ip_address)}
                        className="unblock-btn"
                      >
                        <FontAwesomeIcon icon={faUnlock} /> Unblock
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderStatistics = () => (
    <div className="security-statistics">
      <div className="stats-header">
        <h3><FontAwesomeIcon icon={faServer} /> Security Statistics</h3>
        <div className="stats-controls">
          <select 
            value={statsDays} 
            onChange={(e) => setStatsDays(Number(e.target.value))}
            className="period-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button onClick={loadStatistics} className="refresh-btn">
            <FontAwesomeIcon icon={faRefresh} /> Refresh
          </button>
          <button onClick={handleCleanupLogs} className="cleanup-btn">
            <FontAwesomeIcon icon={faTrash} /> Cleanup Old Logs
          </button>
        </div>
      </div>

      {statistics && (
        <div className="stats-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h4>Event Statistics</h4>
              <div className="event-stats">
                {statistics.event_statistics.map((stat: any, index: number) => (
                  <div key={index} className="event-stat">
                    <span className="event-name">{stat.event_type}</span>
                    <span className={`event-count ${stat.success ? 'success' : 'failed'}`}>
                      {stat.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stat-card">
              <h4>Risk Level Distribution</h4>
              <div className="risk-stats">
                {statistics.risk_level_distribution.map((stat: any, index: number) => (
                  <div key={index} className={`risk-stat risk-${stat.risk_level}`}>
                    <span className="risk-name">{stat.risk_level}</span>
                    <span className="risk-count">{stat.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stat-card">
              <h4>Top Threat IPs</h4>
              <div className="threat-stats">
                {statistics.top_failed_ips.map((ip: any, index: number) => (
                  <div key={index} className="threat-ip">
                    <span className="ip-address">{ip.ip_address}</span>
                    <span className="failed-count">{ip.failed_count} failures</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stat-card">
              <h4>Block Summary</h4>
              <div className="block-stats">
                <div className="block-stat">
                  <span className="label">Blocks Created:</span>
                  <span className="value">{statistics.blocks_created}</span>
                </div>
                <div className="block-stat">
                  <span className="label">Currently Active:</span>
                  <span className="value">{statistics.active_blocks}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <div className="security-management">
      <div className="security-header">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> Security Management</h2>
        <div className="security-tabs">
          <button 
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            <FontAwesomeIcon icon={faChartLine} /> Dashboard
          </button>
          <button 
            className={activeTab === 'audit' ? 'active' : ''}
            onClick={() => { setActiveTab('audit'); loadAuditLogs(); }}
          >
            <FontAwesomeIcon icon={faEye} /> Audit Logs
          </button>
          <button 
            className={activeTab === 'blocked' ? 'active' : ''}
            onClick={() => { setActiveTab('blocked'); loadBlockedIPs(); }}
          >
            <FontAwesomeIcon icon={faBan} /> Blocked IPs
          </button>
          <button 
            className={activeTab === 'statistics' ? 'active' : ''}
            onClick={() => { setActiveTab('statistics'); loadStatistics(); }}
          >
            <FontAwesomeIcon icon={faServer} /> Statistics
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading && (
        <div className="loading-spinner">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
        </div>
      )}

      <div className="security-content">
        {tabContent()}
      </div>
    </div>
  );
};

export default SecurityManagement;
